import { forwardRef, InputHTMLAttributes, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import InputError from './InputError';

interface LabeledInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  showPasswordToggle?: boolean;
}

const LabeledInput = forwardRef<HTMLInputElement, LabeledInputProps>(
  (
    {
      label,
      error,
      type,
      className = '',
      showPasswordToggle = false,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const shouldShowPasswordToggle = showPasswordToggle && type === 'password';

    const inputType =
      shouldShowPasswordToggle && isPasswordVisible ? 'text' : type;

    const toggleLabel = isPasswordVisible ? 'Hide password' : 'Show password';

    return (
      <fieldset className="form-control w-full">
        <legend className="label">
          <span className="fieldset-legend text-primary font-semibold text-sm">
            {label}
          </span>
        </legend>

        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            className={`input input-lg bg-white placeholder:text-[var(--light-gray)] w-full ${
              shouldShowPasswordToggle ? 'pr-12' : ''
            } ${error ? 'input-error border-error' : ''} ${className}`}
          />

          {shouldShowPasswordToggle && (
            <button
              type="button"
              aria-label={toggleLabel}
              title={toggleLabel}
              onClick={() => setIsPasswordVisible((current) => !current)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary rounded-md p-1"
            >
              {isPasswordVisible ? (
                <Eye className="h-5 w-5" />
              ) : (
                <EyeOff className="h-5 w-5" />
              )}
            </button>
          )}
        </div>

        <InputError error={error} />
      </fieldset>
    );
  }
);

LabeledInput.displayName = 'LabeledInput';

export default LabeledInput;
